import Modal from "../Modal";
import GButton from "../base/GButton";
import { useMutation } from "@apollo/client";
import { SPIN_HOURLY_BONUS } from "../../../graphql/bonuses";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
interface SpinnerSegment {
    amount: number;
    label: string;
    color: string;
    type: string;
}

const HourlyPopup = ({
    isOpen,
    onClose,
    isEligable,
    reason,
    bonusData
}: {
    isOpen: boolean;
    onClose: () => void;
    isEligable: boolean;
    reason: string | undefined;
    bonusData: any;
}) => {
    const { t } = useTranslation();
    const [spinHourlyBonus, { data: spinHourlyBonusData, loading: spinHourlyBonusLoading, error: spinHourlyBonusError }] = useMutation(SPIN_HOURLY_BONUS);
    const [isSpinning, setIsSpinning] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [winAmount, setWinAmount] = useState<number | null>(null);
    const [winLabel, setWinLabel] = useState<string | null>(null);
    const spinnerRef = useRef<SVGSVGElement>(null);

    // Color palette for segments
    const colors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181', '#AA96DA', '#FCBAD3', '#A8E6CF', '#FF9999', '#66D9EF'];

    // Generate segments from bonusData or use defaults
    console.log({ bonusData })
    const segments: SpinnerSegment[] = bonusData?.bonus?.conditions?.rewards
        ? bonusData.bonus.conditions.rewards.map((reward: any, index: number) => ({
            amount: reward.amount,
            label: reward.label,
            color: reward.type === 'nothing' ? '#666666' : colors[index % colors.length],
            type: reward.type
        }))
        : [
            { amount: 0.2, label: "0.2 USD", probability: 0.4, type: "fixed", color:'#FF6B6B' },
            { amount: 0.15, label: "0.15 USD", probability: 0.1, type: "fixed", color:'#4ECDC4' },
            { amount: 0.5, label: "0.5 USD", probability: 0.3, type: "fixed", color:'#FFE66D' },
            { amount: 1, label: "1 USD", probability: 0.15, type: "fixed", color:'#95E1D3' },
            { amount: 5, label: "5 USD", probability: 0.05, type: "fixed", color:'#F38181' },
            { label: "Nothing", probability: 0, type: "nothing", color:'#AA96DA' },
        ];

    const handleSpin = async () => {
        if (isSpinning || !isEligable) return;
        if (winAmount) {
            onClose();
            return;
        };
        setIsSpinning(true);
        setWinAmount(null);
        setWinLabel(null);

        try {
            // Call the mutation
            const result = await spinHourlyBonus();

            if (result.data?.spinHourlyBonus?.__typename === 'BonusClaimSuccessResult') {
                const wonAmount = result.data.spinHourlyBonus.amount;
                setWinAmount(wonAmount);

                // Find the index of the winning segment
                let winningIndex = segments.findIndex(seg => seg.amount === wonAmount);

                // Store the winning label
                if (winningIndex !== -1) {
                    setWinLabel(segments[winningIndex].label);
                }

                // If the winning amount is not in segments, pick a random segment
                if (winningIndex === -1) {
                    winningIndex = Math.floor(Math.random() * segments.length);
                }

                const segmentAngle = 360 / segments.length;

                // Calculate final rotation to land on winning segment
                // With the offset applied, segment centers are at: winningIndex * segmentAngle
                // Pointer is at 0 degrees (top), so rotate by negative angle to align
                const fullRotations = 5 + Math.floor(Math.random() * 3);
                const targetAngle = -(winningIndex * segmentAngle);
                const finalRotation = (fullRotations * 360) + targetAngle;

                setRotation(finalRotation);

                // Stop spinning after animation completes
                setTimeout(() => {
                    setIsSpinning(false);
                }, 5000);
            } else if (result.data?.spinHourlyBonus?.__typename === 'BonusClaimUsedResult') {
                // Handle case where bonus was already used
                setIsSpinning(false);
            }
        } catch (error) {
            console.error('Error spinning:', error);
            setIsSpinning(false);
        }
    };

    // Reset when modal closes
    useEffect(() => {
        if (!isOpen) {
            setRotation(0);
            setWinAmount(null);
            setWinLabel(null);
            setIsSpinning(false);
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('popup.hourlyBonusTitle')} subTitle={t('popup.hourlyBonusSubtitle')}>
            <div className="flex items-center justify-center flex-col w-full  md:py-8 py-2">
                {!isEligable ? (
                    <div className="text-center justify-center items-center flex flex-col">
                        <p className="text-[#fff] text-[20px] mb-4 !font-[800] capitalize">{reason || t('popup.hourlyOnceAnHour')}</p>
                        <GButton className="md:mt-4 mt-2 md:py-[20px] py-[10px] md:px-6 px-4 w-full" onClick={onClose}>{t('popup.close')}</GButton>
                    </div>
                ) : (
                    <>
                        {/* Spinner Container */}
                        <div className="relative mb-8 w-full max-w-[400px] mx-auto">
                            {/* Pointer/Arrow */}
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-20">
                                <div className="flex flex-col items-center">
                                    <div className="w-0 h-0 border-l-[15px] sm:border-l-[25px] border-l-transparent border-r-[15px] sm:border-r-[25px] border-r-transparent border-t-[30px] sm:border-t-[50px] border-t-[#0086D3] filter drop-shadow-lg" />
                                    <div className="w-1 h-3 sm:h-4 bg-[#0086D3] -mt-1"></div>
                                </div>
                            </div>

                            {/* Spinner Wheel - SVG approach for perfect segments */}
                            <div className="relative w-full aspect-square">
                                <svg
                                    ref={spinnerRef}
                                    width="100%"
                                    height="100%"
                                    viewBox="0 0 400 400"
                                    className="rounded-full shadow-2xl w-full h-full"
                                    style={{
                                        transform: `rotate(${rotation}deg)`,
                                        transition: isSpinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
                                    }}
                                >
                                    {/* Background circle */}
                                    <circle cx="200" cy="200" r="200" fill="#1a1a1a" />

                                    {segments.map((segment, index) => {
                                        const segmentAngle = 360 / segments.length;
                                        const startAngle = (index * segmentAngle) - 90 - (segmentAngle / 2);
                                        const endAngle = startAngle + segmentAngle;

                                        // Convert to radians
                                        const startRad = (startAngle * Math.PI) / 180;
                                        const endRad = (endAngle * Math.PI) / 180;

                                        // Calculate path points
                                        const x1 = 200 + 195 * Math.cos(startRad);
                                        const y1 = 200 + 195 * Math.sin(startRad);
                                        const x2 = 200 + 195 * Math.cos(endRad);
                                        const y2 = 200 + 195 * Math.sin(endRad);

                                        const largeArc = segmentAngle > 180 ? 1 : 0;

                                        // Text position - perfectly centered in each segment
                                        // Calculate the exact middle angle of the segment
                                        const midAngle = (index * segmentAngle);
                                        // Position at 65% of radius (optimal centroid for pie segments)
                                        const textDistance = 127;
                                        const textAngleRad = (midAngle * Math.PI) / 180;
                                        const textX = 200 + textDistance * Math.sin(textAngleRad);
                                        const textY = 200 - textDistance * Math.cos(textAngleRad);

                                        // Rotate text to align with segment direction
                                        const textRotation = midAngle + 90;

                                        return (
                                            <g key={index}>
                                                {/* Segment path */}
                                                <path
                                                    d={`M 200 200 L ${x1} ${y1} A 195 195 0 ${largeArc} 1 ${x2} ${y2} Z`}
                                                    fill={segment.color}
                                                    stroke="rgba(255,255,255,0.2)"
                                                    strokeWidth="1"
                                                />
                                                {/* Text */}
                                                <text
                                                    x={textX}
                                                    y={textY}
                                                    fill="white"
                                                    fontSize="14"
                                                    fontWeight="900"
                                                    textAnchor="middle"
                                                    dominantBaseline="middle"
                                                    transform={`rotate(${textRotation}, ${textX}, ${textY})`}
                                                    style={{
                                                        filter: 'drop-shadow(2px 2px 3px rgba(0,0,0,0.9))',
                                                        textShadow: '0 0 3px rgba(0,0,0,1)'
                                                    }}
                                                    className="text-[10px] sm:text-[14px]"
                                                >
                                                    {segment.label}
                                                </text>
                                            </g>
                                        );
                                    })}

                                    {/* Outer border ring */}
                                    <circle
                                        cx="200"
                                        cy="200"
                                        r="196"
                                        fill="none"
                                        stroke="rgba(255,255,255,0.15)"
                                        strokeWidth="8"
                                    />
                                </svg>

                                {/* Center Circle */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#0086D3] to-[#005a8f] rounded-full border-2 sm:border-4 border-white shadow-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-xs sm:text-sm">{t('popup.spin')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Win Amount Display */}
                        {winAmount !== null && !isSpinning && (
                            <div className="mb-6 text-center animate-bounce px-4">
                                {winAmount > 0 ? (
                                    <>
                                        <p className="text-[#0086D3] text-lg sm:text-2xl font-bold mb-2">{t('popup.congratulations')}</p>
                                        <p className="text-white text-2xl sm:text-4xl font-extrabold">
                                            {t('popup.youWon', { prize: winLabel || `$${winAmount}` })}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-gray-400 text-lg sm:text-2xl font-bold mb-2">{t('popup.betterLuck')}</p>
                                        <p className="text-white text-xl sm:text-3xl font-extrabold">
                                            {winLabel || t('popup.nothingThisTime')}
                                        </p>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Spin Button */}
                        <GButton
                            onClick={handleSpin}
                            disabled={isSpinning || spinHourlyBonusLoading}
                            className="px-8 sm:px-12 py-3 text-base sm:text-lg font-bold w-full max-w-xs"
                        >
                            {isSpinning ? t('popup.spinning') : winAmount ? t('popup.claimReward') : t('popup.spinNow')}
                        </GButton>

                        {/* Error Display */}
                        {spinHourlyBonusError && (
                            <p className="text-red-400 mt-4">
                                Error: {spinHourlyBonusError.message}
                            </p>
                        )}

                        {/* Loading State */}
                        {spinHourlyBonusLoading && (
                            <p className="text-[#B3BEC1] mt-4">{t('popup.processingSpin')}</p>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default HourlyPopup;